/**
 * Layout options — CLAUDE.md §5: separate from style tokens. These switch
 * *component variants*, not CSS. Every option is a closed enum and v1 caps the
 * list at six, because each new option multiplies the visual test matrix.
 *
 * There is no drag-and-drop page builder. Ever.
 */

export const PRODUCT_LIST_LAYOUTS = ['grid-2', 'grid-3', 'list', 'list-text'] as const;
export const CARD_IMAGE_RATIOS = ['1:1', '3:4'] as const;
export const CARD_SHOW_FIELDS = ['name', 'price', 'compare_at', 'badge', 'variants'] as const;
export const CATEGORIES_NAV = ['chips', 'tabs', 'none'] as const;
/** `bottom-sheet` is reserved for the future F&B vertical (§4). */
export const PRODUCT_DETAIL_LAYOUTS = ['gallery-first', 'info-first', 'bottom-sheet'] as const;

export type ProductListLayout = (typeof PRODUCT_LIST_LAYOUTS)[number];
export type CardImageRatio = (typeof CARD_IMAGE_RATIOS)[number];
export type CardShowField = (typeof CARD_SHOW_FIELDS)[number];
export type CategoriesNav = (typeof CATEGORIES_NAV)[number];
export type ProductDetailLayout = (typeof PRODUCT_DETAIL_LAYOUTS)[number];

export interface LayoutConfig {
  product_list: ProductListLayout;
  card_image_ratio: CardImageRatio;
  card_show: CardShowField[];
  categories_nav: CategoriesNav;
  home_banner: boolean;
  product_detail: ProductDetailLayout;
}

export type PartialLayoutConfig = Partial<LayoutConfig>;

export const DEFAULT_LAYOUT: LayoutConfig = {
  product_list: 'grid-2',
  card_image_ratio: '3:4',
  card_show: ['name', 'price'],
  categories_nav: 'chips',
  home_banner: true,
  product_detail: 'gallery-first',
};

/**
 * Vertical config constrains which enum values are allowed (§4/§5) — retail
 * never gets the F&B bottom sheet, F&B never gets 3:4 fashion cards.
 */
export const ALLOWED_LAYOUT_BY_VERTICAL: Record<
  'retail' | 'fnb' | 'services' | 'digital',
  { product_detail: readonly ProductDetailLayout[]; card_image_ratio: readonly CardImageRatio[] }
> = {
  retail: { product_detail: ['gallery-first', 'info-first'], card_image_ratio: ['1:1', '3:4'] },
  fnb: { product_detail: ['bottom-sheet', 'info-first'], card_image_ratio: ['1:1'] },
  services: { product_detail: ['info-first'], card_image_ratio: ['1:1'] },
  /**
   * §4 `digital`: the product is a code, not an object. There is nothing to
   * photograph, so the 3:4 portrait card built for garments leaves a tall
   * empty box, and a gallery-first detail page leads with an image the buyer
   * has no reason to study. What they read is the denomination and the terms.
   */
  digital: { product_detail: ['info-first'], card_image_ratio: ['1:1'] },
};

export function constrainLayout(
  layout: LayoutConfig,
  vertical: 'retail' | 'fnb' | 'services' | 'digital',
): LayoutConfig {
  const allowed = ALLOWED_LAYOUT_BY_VERTICAL[vertical];
  return {
    ...layout,
    product_detail: allowed.product_detail.includes(layout.product_detail)
      ? layout.product_detail
      : allowed.product_detail[0]!,
    card_image_ratio: allowed.card_image_ratio.includes(layout.card_image_ratio)
      ? layout.card_image_ratio
      : allowed.card_image_ratio[0]!,
  };
}

/** Aspect ratio as a CSS value for the card image slot. */
export function ratioToCss(ratio: CardImageRatio): string {
  return ratio === '1:1' ? '1 / 1' : '3 / 4';
}
